import { Button, Flex, Form, Input, message, TreeSelect } from "antd";
import { useEffect, useState } from "react";
import { fetchOrganization } from "../Service/organization";

/* ------------------ Helpers ------------------ */

// Build parent → children tree
const buildTreeFromData = (data) => {
  const map = {};
  const tree = [];

  data.forEach((item) => {
    map[item.uuid] = { ...item, children: [] };
  });

  data.forEach((item) => {
    if (item.parent?.uuid && map[item.parent.uuid]) {
      map[item.parent.uuid].children.push(map[item.uuid]);
    } else {
      tree.push(map[item.uuid]);
    }
  });

  return tree;
};

// Convert to AntD TreeSelect format
const convertTreeData = (nodes, excludeUuid) =>
  nodes
    .filter((node) => node.uuid !== excludeUuid)
    .map((node) => ({
      title: node.name,
      value: node.uuid, // MUST match form value
      key: node.uuid,
      children: node.children?.length
        ? convertTreeData(node.children, excludeUuid)
        : undefined,
    }));

/* ------------------ Component ------------------ */

const OrganizationForm = (props) => {
  const [form] = Form.useForm();
  const [organizationTree, setOrganizationTree] = useState([]);

  /* -------- Fetch Organizations -------- */

  const fetchData = async () => {
    try {
      const res = await fetchOrganization();
      const organizations = res.data || [];

      if (!Array.isArray(organizations)) {
        message.error("Invalid response format");
        return;
      }

      setOrganizationTree(organizations);
    } catch (err) {
      message.error("Network error while fetching organizations");
    }
  };

  /* -------- Edit / Reset Handling -------- */

  useEffect(() => {
    if (props.editData) {
      form.setFieldsValue({
        name: props.editData.name,
        contactNo: props.editData.contactNo,
        email: props.editData.email,
        website: props.editData.website,
        logoUrl: props.editData.logoUrl,
        parent: props.editData.parent?.uuid
          ? { uuid: props.editData.parent.uuid }
          : null,
      });
    } else {
      form.resetFields();
    }

    fetchData();
  }, [props.editData]);

  /* -------- Refetch when modal opens -------- */

  useEffect(() => {
    if (props.isModalOpen) {
      fetchData();
    }
  }, [props.isModalOpen]);

  /* ------------------ Render ------------------ */

  return (
    <Form
      form={form}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 16 }}
      style={{ maxWidth: 600, margin: "0 auto" }}
      onFinish={(data) =>
        props.saveData(data).then((success) => {
          if (success) {
            fetchData();
            form.resetFields();
          }
        })
      }
    >
      {/* Organization Name */}
      <Form.Item
        label="Organization Name"
        name="name"
        rules={[{ required: true, message: "Please input organization name!" }]}
      >
        <Input placeholder="Enter organization name" allowClear />
      </Form.Item>

      {/* Contact Number */}
      <Form.Item
        label="Contact Number"
        name="contactNo"
        rules={[
          { required: true, message: "Please input contact number!" },
          {
            pattern: /^[0-9+\- ]{7,15}$/,
            message: "Enter a valid contact number",
          },
        ]}
      >
        <Input placeholder="Enter contact number" allowClear />
      </Form.Item>

      {/* Email */}
      <Form.Item
        label="Email"
        name="email"
        rules={[
          { required: true, message: "Please input email!" },
          { type: "email", message: "Enter a valid email address!" },
        ]}
      >
        <Input placeholder="Enter email address" allowClear />
      </Form.Item>

      {/* Website */}
      <Form.Item
        label="Website"
        name="website"
        rules={[
          { required: true, message: "Please input website!" },
          {
            pattern:
              /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-]*)*(\?.*)?(#.*)?$/,
            message: "Enter a valid website URL",
          },
        ]}
      >
        <Input placeholder="https://example.com" allowClear />
      </Form.Item>

      {/* Logo URL */}
      <Form.Item
        label="Logo URL"
        name="logoUrl"
        rules={[{ required: true, message: "Please input logo URL!" }]}
      >
        <Input placeholder="Enter logo image URL" allowClear />
      </Form.Item>

      {/* Parent Organization */}
      <Form.Item label="Parent Organization" name={["parent", "uuid"]}>
        <TreeSelect
          treeData={convertTreeData(
            buildTreeFromData(organizationTree),
            props.editData?.uuid
          )}
          placeholder="Select parent organization"
          allowClear
          showSearch
          treeDefaultExpandAll={false}
        />
      </Form.Item>

      {/* Submit Button */}
      <Flex justify="end">
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Flex>
    </Form>
  );
};

export default OrganizationForm;
